import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink } from 'react-router-dom';

import { Avatar } from './Avatar';
import { Logo } from './Logo';
import { NotificationBell } from './NotificationBell';
import { ChallengeIcon, CommunityIcon, DotsIcon, HomeIcon, TrophyIcon } from './icons';
import { useAuth } from '../auth/AuthContext';
import { assetUrl } from '../lib/api';

/**
 * El marco de las pantallas de adentro.
 *
 * LA NAVEGACIÓN ESTÁ ABAJO Y NO ARRIBA, y es el cambio grande: cinco
 * pestañas —Inicio, Clubes, Torneos, Retos y Más— fijas al borde inferior.
 *
 * POR QUÉ ABAJO. Esto se usa parado en la cancha, con una mano. Arriba de un
 * teléfono es justo donde el pulgar no llega, y los enlaces que estaban ahí
 * competían por el mismo renglón que la campanita y el nombre: en un teléfono
 * angosto la barra se partía en dos filas y el orden cambiaba solo.
 *
 * ARRIBA QUEDA LO QUE NO ES NAVEGACIÓN: la campanita —lo único de la pantalla
 * que puede pedir algo— y quién sos. Cerrar sesión se fue a «Más», con el
 * perfil: es algo que se hace una vez cada mucho, y tenerlo al lado del
 * nombre lo ponía a un dedo de distancia de nada.
 */
export function AppLayout({ children }: { children: ReactNode }): JSX.Element {
  const { t } = useTranslation();
  const { session } = useAuth();
  const user = session?.user;

  return (
    <div className="page">
      {/*
        LA BARRA DE ARRIBA, EN DOS LADOS.

        IZQUIERDA, DE QUIÉN ES ESTO: el isotipo y la palabra. DERECHA, QUIÉN
        LA ESTÁ USANDO y lo único que puede pedirle algo: la campanita, la
        foto y el nombre. Antes todo eso estaba amontonado a la izquierda y
        la campana sola a la derecha, así que la marca y la persona se leían
        como una sola cosa.

        LA PALABRA VA EN LA LETRA DE LA APLICACIÓN, no en la del archivo del
        logo, y es una decisión: el logo aprobado es APILADO —el isotipo
        arriba y la palabra debajo— y recortarle la palabra para ponerla al
        lado sería armar una versión horizontal que la guía no trae. «Retos»
        toma el color del texto y «CR» el verde de la marca, que es como está
        dibujado.
      */}
      <header className="topbar app-topbar">
        {/*
          LOS DOS LADOS DE LA BARRA LLEVAN A ALGÚN LADO.

          La marca al inicio y la foto al perfil. Es lo que hace toda
          aplicación y por eso se toca sin pensarlo; antes las dos eran
          adorno, y tocarlas no hacía nada — que se siente como que la
          pantalla no responde, no como que ahí no había nada.

          LA CAMPANITA QUEDA AFUERA del enlace del perfil: abre su propia
          lista, y un botón adentro de un enlace no es HTML válido —el
          navegador no sabe cuál de los dos se apretó—.
        */}
        {/* SE LLAMA COMO SE VE, «Tennis Up Club», y no «Inicio»: abajo ya hay una
            pestaña que se llama Inicio, y dos enlaces con el mismo nombre en
            la misma pantalla obligan a adivinar cuál es cuál a quien navega
            con un lector de pantalla. */}
        <Link className="app-identity" to="/" aria-label={t('app.name')}>
          <Logo size={36} />
          {/*
            LA PALABRA VA ESCRITA ACÁ Y NO ADENTRO DEL SVG, a propósito: en la
            barra el logotipo completo quedaría de ocho píxeles de alto y la
            palabra se leería como una mancha. Isotipo grande + palabra en
            texto de verdad, que además se achica sola en un teléfono angosto.

            `aria-hidden` porque el enlace ya se llama «Tennis Up Club» por su
            `aria-label`: sin esto, un lector de pantalla lo diría dos veces.
          */}
          <span className="app-wordmark" aria-hidden="true">
            Tennis<span> Up</span> Club
          </span>
        </Link>

        <div className="app-viewer">
          <NotificationBell />
          {/*
            A «MÁS» Y NO AL PERFIL. La esquina de arriba es «lo mío», y lo mío
            son los ajustes: el perfil es UNO de ellos y está ahí adentro, en
            el primer enlace. Llevando derecho al formulario del perfil, el
            tema y salir de la cuenta quedaban solo en la pestaña de abajo.

            SE LLAMA «Mi cuenta» Y NO «Más», aunque lleve ahí: la barra de
            abajo ya tiene una pestaña llamada «Más», y dos enlaces con el
            mismo nombre en la misma pantalla obligan a adivinar cuál es cuál
            a quien navega con un lector de pantalla.
          */}
          {user !== undefined && (
            <Link className="app-me" to="/mas" aria-label={t('nav.account')}>
              <Avatar url={assetUrl(user.avatarPath)} name={user.displayName} size={28} />
              {/*
                EL NOMBRE SE PARTE ANTES QUE EMPUJAR. A 320 px, con la
                palabra a la izquierda y la campana y la foto acá, lo que
                sobra para el nombre son unos 70 px: «Wilberth Céspedes» no
                entra de ninguna forma. Se corta con puntos suspensivos —la
                foto y la inicial siguen diciendo quién es— en vez de estirar
                la barra y sacar la marca de la pantalla.
              */}
              <span className="app-name">{user.displayName}</span>
            </Link>
          )}
        </div>
      </header>

      <main className="card">{children}</main>

      {/*
        LAS CINCO PESTAÑAS.

        Eran cuatro y ahora son cinco. A 320 px cada una pasó de 80 px a 64, y
        eso NO SE ADIVINÓ: se midió en Chromium, en los nueve idiomas, y por
        eso la letra baja un punto más en pantallas angostas (ver la regla
        `@media (max-width: 420px)` en styles-layout.css).

        CINCO ES EL TECHO. Con seis, «Torneos» no entra en ningún idioma y la
        barra se vuelve una fila de dibujos que hay que adivinar.

        Cada una lleva ÍCONO Y PALABRA. El ícono solo se adivina mal —el mismo
        dibujo es «torneos» para quien lo hizo y «premio» para el resto— y la
        palabra sola, a esta escala, se lee lenta.
      */}
      <nav className="tabbar" aria-label={t('nav.tabs')}>
        <NavLink to="/" end>
          <HomeIcon title="" size={22} />
          <span>{t('nav.home')}</span>
        </NavLink>
        <NavLink to="/clubes">
          <CommunityIcon title="" size={22} />
          <span>{t('nav.communities')}</span>
        </NavLink>
        <NavLink to="/torneos">
          <TrophyIcon title="" size={22} />
          <span>{t('nav.tournaments')}</span>
        </NavLink>
        <NavLink to="/retos">
          <ChallengeIcon title="" size={22} />
          <span>{t('nav.challenges')}</span>
        </NavLink>
        <NavLink to="/mas">
          <DotsIcon title="" size={22} />
          <span>{t('nav.more')}</span>
        </NavLink>
      </nav>
    </div>
  );
}
